import { useEffect, useState } from 'react';
import { useFinancialContexts } from '../../../contexts/ContextsContext';
import { contextApi, ContextBalances, FinancialContext } from '../../../services/contextApi';
import { transactionApi } from '../../../services/transactionApi';
import { canManageMembers } from '../../../utils/contextPermissions';

/** Coming back to the tab fires both "focus" and "visibilitychange": one look is enough. */
const RECHECK_GAP_MS = 1000;

interface Options {
  /** The group's balances as the page has them; a fresh copy means the people may have changed. */
  balances: ContextBalances | null;
  /** Asks the page to load the balances again, for whoever joined while the user was away. */
  onRefresh?: () => void;
}

/** A number read for one group: switching groups never shows the last one's answer. */
type Counted = { groupId: string; total: number } | null;

const countEntries = async (groupId: string): Promise<number> =>
  (await transactionApi.getTransactions({ contextId: groupId, page: 1, limit: 1 })).total;

/**
 * Groups seen with an entry on this device. A group never loses its first
 * entry, so their Início opens on the dashboard at once, with no count to
 * wait for. Read each time, never kept in memory: storage may be off (a
 * private window), and then every visit counts.
 */
const ACTIVE_GROUPS_KEY = 'financy.activeGroups';

const activeGroups = (): string[] => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(ACTIVE_GROUPS_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const rememberActive = (groupId: string) => {
  const known = activeGroups();
  if (known.includes(groupId)) return;
  try {
    window.localStorage.setItem(ACTIVE_GROUPS_KEY, JSON.stringify([...known, groupId].slice(-200)));
  } catch {
    // Storage off: the next visit counts again.
  }
};

/**
 * What a group's Início needs to choose between "Monte o grupo" and the
 * dashboard: how many entries the group has ever had (null while loading)
 * and, for owners and admins, the invitations to sign up still open (null
 * while loading). While the group has no entry, coming back to the tab
 * looks again, and refreshes the group list and the balances: an expense
 * sent in Telegram, a link made there or someone who joined shows up
 * without reloading the page.
 */
export const useGroupSetup = (group: FinancialContext, { balances, onRefresh }: Options) => {
  const groupId = group.id;
  const manages = canManageMembers(group.memberRole);
  const { refreshQuietly } = useFinancialContexts();
  const [count, setCount] = useState<Counted>(null);
  const [invitations, setInvitations] = useState<Counted>(null);
  const counted = count?.groupId === groupId ? count.total : null;
  const entries = counted ?? (activeGroups().includes(groupId) ? 1 : null);
  const settingUp = entries === 0;
  // Until the count is back, a group not known to have entries may still be setting up.
  const mayBeSettingUp = entries === null || entries === 0;

  useEffect(() => {
    if (activeGroups().includes(groupId)) return undefined;
    let cancelled = false;
    countEntries(groupId).then(
      (total) => {
        if (total > 0) rememberActive(groupId);
        if (!cancelled) setCount({ groupId, total });
      },
      // Unknown: the dashboard, which shows its own error.
      () => {
        if (!cancelled) setCount({ groupId, total: 1 });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  useEffect(() => {
    if (!settingUp) return undefined;
    let cancelled = false;
    let last = 0;
    const recheck = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < RECHECK_GAP_MS) return;
      last = Date.now();
      countEntries(groupId)
        .then((total) => {
          if (total > 0) rememberActive(groupId);
          if (!cancelled) setCount({ groupId, total });
        })
        // Offline for a moment: the next visit looks again.
        .catch(() => undefined);
      refreshQuietly().catch(() => undefined);
      onRefresh?.();
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, [settingUp, groupId, refreshQuietly, onRefresh]);

  // Someone invited by email with no account yet is only in this list, not
  // in the balances. Read alongside the count, so "Monte o grupo" shows with
  // its steps already right; read again with the balances, which the page
  // reloads when Membros closes and when the user comes back.
  useEffect(() => {
    if (!manages || !mayBeSettingUp) return undefined;
    let cancelled = false;
    contextApi.getSignupInvitations(groupId).then(
      (list) => {
        const now = Date.now();
        // An expired invitation lets nobody in.
        const open = list.filter((invitation) => new Date(invitation.expiresAt).getTime() > now).length;
        if (!cancelled) setInvitations({ groupId, total: open });
      },
      // Unknown: counted as none, rather than keeping the page waiting.
      () => {
        if (!cancelled) setInvitations((current) => (current?.groupId === groupId ? current : { groupId, total: 0 }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [manages, mayBeSettingUp, groupId, balances]);

  return {
    entries,
    /** null while an owner's or admin's open invitations are still being read. */
    signupInvitations: !manages ? 0 : invitations?.groupId === groupId ? invitations.total : null,
  };
};
